import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { DateRangePicker as ReactDateRangePicker } from 'react-dates';
import 'react-dates/initialize';
import type {
  MenuListProps,
  OptionProps,
  SingleValueProps,
} from 'react-select';
import { components } from 'react-select';
import InsightsRestriction from '../admin/insights/InsightsRestriction';
import InsightsTimeOption from '../admin/insights/InsightsTimeOption';
import useAppSelector from '../app/redux/useAppSelector';
import type { PlanTier } from '../graphql/generated/graphqlTypes';
import { UsageAnalyticsDays } from '../organization/PricingConfig';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import Caret from './Caret';
import styles from './DateRangePicker.module.scss';
import PopOver from './deprecated/PopOver';
import { PopOverContainerArrow } from './deprecated/PopOverContainer';
import Select from './Select';

type TimeRange = {
  label: string;
  value: number | 'custom';
};

type TimeRanges = readonly { options: readonly TimeRange[] }[];

const DEFAULT_TIME_RANGES = [
  {
    options: [
      { label: '7 days', value: 7 },
      { label: '14 days', value: 14 },
      { label: '30 days', value: 30 },
      { label: '90 days', value: 90 },
    ],
  },
  {
    options: [{ label: 'Custom', value: 'custom' }],
  },
] as const;

type Dates = {
  startAt: moment.Moment | null;
  endAt: moment.Moment | null;
};

const getNewDates = ({
  startDate,
  endDate,
  defaultStartAt,
  defaultEndAt,
}: {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  defaultStartAt?: never;
  defaultEndAt?: never;
}): Dates => {
  const newStartAt = startDate?.startOf('day') ?? defaultStartAt;

  let newEndAt = endDate?.startOf('day') ?? defaultEndAt;

  if (startDate && newStartAt && newEndAt && newStartAt > newEndAt) {
    newEndAt = startDate.startOf('day');
  }

  return {
    startAt: newStartAt ?? null,
    endAt: newEndAt ?? null,
  };
};

const getDatesForNumberOfDays = (days: number) =>
  getNewDates({
    startDate: moment().utc().subtract(days, 'd'),
    endDate: moment().utc().subtract(1, 'd'),
  });

export const DEFAULT_TIME_RANGE = DEFAULT_TIME_RANGES[0].options[2].value;
export const DEFAULT_DATES = getDatesForNumberOfDays(DEFAULT_TIME_RANGE) as {
  startAt: moment.Moment;
  endAt: moment.Moment;
};

interface DateRangePickerProps {
  ranges?: TimeRanges;
  onDatesChange: (dates: {
    startAt: moment.Moment;
    endAt: moment.Moment;
  }) => void;
  firstAllowedDate?: moment.Moment;
  initialRange?: TimeRange['value'];
}

const DateRangePicker = ({
  ranges = DEFAULT_TIME_RANGES,
  onDatesChange,
  firstAllowedDate,
  initialRange = DEFAULT_TIME_RANGE,
}: DateRangePickerProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  ) as PlanTier;

  const [timeRange, setTimeRange] = useState<TimeRange['value']>(initialRange);

  const [dates, setDates] = useState<Dates>(
    getDatesForNumberOfDays(typeof timeRange === 'number' ? timeRange : 7),
  );

  const [focusedInput, setFocusedInput] = useState<
    'startDate' | 'endDate' | null
  >(null);

  const isLarge = useIsSize(Sizes.LG);

  const handleChangeDates = (newDates: Dates) => {
    setDates(newDates);
    if (newDates.startAt && newDates.endAt) {
      onDatesChange({ startAt: newDates.startAt, endAt: newDates.endAt });
    }
  };

  const handleTimeRangeChange = (value: TimeRange['value'] | null) => {
    if (!value) {
      return;
    }
    if (typeof value === 'number') {
      handleChangeDates(getDatesForNumberOfDays(value));
    }
    if (value === 'custom') {
      setFocusedInput('startDate');
    }
    setTimeRange(value);
  };

  const defaultValue = useMemo(
    () =>
      ranges
        .flatMap((r) => r.options)
        .find((range) => range.value === initialRange),
    [ranges, initialRange],
  );

  const datesString = `${dates.startAt?.format('MMM D') ?? ''} - ${
    dates.endAt?.format('MMM D') ?? ''
  }`;

  return (
    <div className={styles.dates}>
      <div className={styles.selectWrapper}>
        <Select<TimeRange, false>
          className={styles.select}
          isClearable={false}
          defaultValue={defaultValue}
          onChange={handleTimeRangeChange}
          menuIsOpen={isMenuOpen}
          options={ranges}
          onMenuOpen={() => setIsMenuOpen(true)}
          onMenuClose={() => setIsMenuOpen(false)}
          isSearchable={false}
          value={timeRange}
          components={{
            // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
            Option: (props: OptionProps<TimeRange, false>) => (
              <InsightsTimeOption
                {...props}
                onUpgradeClick={() => setIsMenuOpen(false)}
              />
            ),
            // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
            MenuList: (props: MenuListProps<TimeRange, false>) => (
              <components.MenuList {...props}>
                <div className={styles.titleContainer}>
                  <div className={styles.title}>
                    {typeof timeRange === 'number'
                      ? `Last ${timeRange} days`
                      : props.getValue()[0]?.label}
                  </div>
                  <div className={styles.titleDates}>{datesString}</div>
                </div>
                {props.children}
              </components.MenuList>
            ),
            // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
            GroupHeading: () => <hr className={styles.groupHeading} />,
            // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
            SingleValue: (props: SingleValueProps<TimeRange, false>) => (
              <components.SingleValue {...props}>
                {typeof timeRange === 'number' ? props.children : datesString}
              </components.SingleValue>
            ),
          }}
        />
        {timeRange === 'custom' && (
          <ReactDateRangePicker
            startDate={dates.startAt ?? null}
            startDateId="start"
            endDate={dates.endAt ?? null}
            endDateId="end"
            onDatesChange={(newDates: {
              startDate: moment.Moment | null;
              endDate: moment.Moment | null;
            }) => {
              if (focusedInput === 'endDate') {
                handleChangeDates(getNewDates(newDates));
              } else {
                setDates(getNewDates(newDates));
              }
            }}
            focusedInput={focusedInput}
            onFocusChange={(input: 'startDate' | 'endDate' | null) => {
              if (!input) {
                // Slight delay for calling click handlers in popover (e.g. upgrade button)
                setTimeout(() => setFocusedInput(input), 10);
                return;
              }
              setFocusedInput(input);
            }}
            isOutsideRange={(day: moment.Moment) =>
              (firstAllowedDate && day < firstAllowedDate) ||
              day > moment().endOf('day').subtract(1, 'd')
            }
            noBorder
            displayFormat="MMM D"
            numberOfMonths={isLarge ? 2 : 1}
            anchorDirection="right"
            verticalSpacing={0}
            daySize={36}
            navPrev={<Caret direction="left" block size={10} />}
            navNext={<Caret direction="right" block size={10} />}
            renderDayContents={(day: moment.Moment) => {
              const dayView = (
                <div className={styles.dayWrapper}>
                  <div className={styles.day}>{day.format('D')}</div>
                </div>
              );
              const isRestricted = firstAllowedDate && day < firstAllowedDate;

              if (isRestricted) {
                return (
                  // @ts-expect-error
                  <PopOver
                    arrow={<PopOverContainerArrow />}
                    placement="top"
                    popOver={
                      <InsightsRestriction
                        onUpgradeClick={() => {
                          setFocusedInput(null);
                        }}
                        olderThan={UsageAnalyticsDays[planTier]}
                      />
                    }
                    preventCloseOnContentClick
                    trigger="hover"
                    popOverPortal={document.body}
                  >
                    {dayView}
                  </PopOver>
                );
              }
              return dayView;
            }}
            hideKeyboardShortcutsPanel
          />
        )}
      </div>
    </div>
  );
};

export default DateRangePicker;
